import React, { useEffect, useState } from 'react';
import api from '../api';
import { Badge, useToast, Modal } from '../ui';
import PhotoInput from '../PhotoInput';
import { useLang } from '../Layout';
import { FaPlus, FaCamera, FaMoneyBillWave } from 'react-icons/fa6';

const T = {
  bn: { myWorkers: 'আমার কর্মী', present: 'উপস্থিত দিন', myStock: 'আমার মাল', history: 'মাল নেওয়ার ইতিহাস', empty: 'এখনো মাল নেওয়া হয়নি।', emptyW: 'কোনো কর্মী নেই।', attendance: 'হাজিরা', bill: 'আমার বিল', noBill: 'এখনো বিল নেই।', week: 'সপ্তাহ', days: 'দিন', newWorker: 'নতুন কর্মী যোগ করুন', name: 'নাম', phone: 'ফোন (ঐচ্ছিক)', rate: 'দৈনিক মজুরি (৳)', add: 'যোগ করুন', added: 'কর্মী যোগ হয়েছে', today: 'আজকের হাজিরা', markToday: 'আজকের হাজিরা দিন', saved: 'হাজিরা সংরক্ষণ হয়েছে', saveAttendance: 'হাজিরা সংরক্ষণ করুন', present: 'উপস্থিত', absent: 'অনুপস্থিত', leave: 'ছুটি', presentToday: 'আজ উপস্থিত', billed: 'বিল (পাবে)', taken: 'নিয়েছে', due: 'বাকি', noWorkerYet: 'আগে কর্মী যোগ করুন — তারপর হাজিরা দিতে পারবেন', todayCount: 'জন কাজ করেছে' },
  en: { myWorkers: 'My Workers', present: 'present days', myStock: 'My Stock', history: 'Material History', empty: 'No materials taken yet.', emptyW: 'No workers yet.', attendance: 'Attendance', bill: 'My Bill', noBill: 'No bills yet.', week: 'Week', days: 'days', newWorker: 'Add New Worker', name: 'Name', phone: 'Phone (optional)', rate: 'Daily wage (৳)', add: 'Add', added: 'Worker added', today: 'Today Attendance', markToday: 'Mark today attendance', saved: 'Attendance saved', saveAttendance: 'Save Attendance', present: 'Present', absent: 'Absent', leave: 'Leave', presentToday: 'Present today', billed: 'Billed (to get)', taken: 'Taken', due: 'Due', noWorkerYet: 'Add workers first, then mark attendance', todayCount: 'working today' },
};

const fmt = (n) => '৳' + Number(n || 0).toLocaleString('bn-BD');

export function MyWorkers() {
  const { lang } = useLang(); const t = T[lang];
  const { toast } = useToast();
  const [list, setList] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: '', phone: '', rate_per_day: '' });
  const [err, setErr] = useState('');

  // today attendance state: { worker_id: { status, photo } }
  const [att, setAtt] = useState({});
  const [savingAtt, setSavingAtt] = useState(false);
  const [presentCount, setPresentCount] = useState(0);

  const load = () => api.get('/contractor/profile').then(({ data }) => {
    setList(data.workers);
    const map = {};
    data.today.attendance.forEach((a) => { if (a.status) map[a.worker_id] = { status: a.status, photo: a.photo || null }; });
    setAtt(map);
    setPresentCount(data.today.present);
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  async function addWorker(e) {
    e.preventDefault(); setErr('');
    try {
      await api.post('/contractor/workers', form);
      toast(t.added, 'success');
      setShow(false); setForm({ name: '', phone: '', rate_per_day: '' }); load();
    } catch (e) { setErr(e.response?.data?.message || 'সমস্যা হয়েছে'); }
  }

  async function saveAttendance() {
    setSavingAtt(true);
    try {
      const records = Object.entries(att).filter(([, v]) => v.status).map(([wid, v]) => ({ worker_id: Number(wid), status: v.status, photo: v.photo || null }));
      if (!records.length) return;
      await api.post('/contractor/attendance/mark', { records });
      toast(t.saved, 'success');
      load();
    } catch (e) { toast(e.response?.data?.message || 'সমস্যা হয়েছে', 'error'); } finally { setSavingAtt(false); }
  }

  const todayCount = Object.values(att).filter((v) => v.status === 'present').length;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h1 className="text-xl font-semibold text-[#0F172A]">{t.myWorkers}</h1>
        <button className="btn-primary text-sm flex items-center gap-2" onClick={() => { setShow(true); setErr(''); }}><FaPlus /> {t.newWorker}</button>
      </div>

      {/* Today attendance banner */}
      <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
          <div>
            <h3 className="font-semibold text-[#0F172A] flex items-center gap-2"><FaCamera className="text-brand-500" /> {t.today}</h3>
            <p className="text-xs text-[#94A3B8]">{todayCount} {t.todayCount} — {list.length}</p>
          </div>
          <button onClick={saveAttendance} disabled={savingAtt || list.length === 0}
            className={`text-xs font-semibold text-white bg-emerald-500 hover:bg-emerald-600 px-3.5 py-2 rounded-lg ${savingAtt ? 'opacity-50' : ''}`}>{t.saveAttendance}</button>
        </div>
        {list.length === 0 && <div className="text-center py-8 text-[#94A3B8] text-sm">{t.noWorkerYet}</div>}
        <div className="space-y-2">
          {list.map((w) => {
            const st = att[w.id]?.status;
            return (
              <div key={w.id} className="flex items-center gap-3 flex-wrap sm:flex-nowrap bg-[#F8FAFC] rounded-xl px-3 py-2">
                <div className="w-8 h-8 rounded-full bg-[#EFF6FF] text-[#3B82F6] flex items-center justify-center text-xs font-bold shrink-0">{w.name[0]}</div>
                <div className="flex-1 min-w-[100px]">
                  <div className="text-sm font-medium text-[#0F172A] truncate">{w.name}</div>
                  <div className="text-[11px] text-[#94A3B8]">{w.present_days} {t.days}</div>
                </div>
                <div className="flex gap-1.5 items-center flex-wrap">
                  {[['present', t.present, 'bg-emerald-50 text-emerald-700 border-emerald-200'], ['leave', t.leave, 'bg-amber-50 text-amber-700 border-amber-200'], ['absent', t.absent, 'bg-rose-50 text-rose-600 border-rose-200']].map(([k, l, cls]) => (
                    <button key={k} onClick={() => setAtt((p) => ({ ...p, [w.id]: { ...(p[w.id] || {}), status: st === k ? '' : k } }))}
                      className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold border transition ${st === k ? cls : 'bg-white text-[#94A3B8] border-[#E2E8F0]'}`}>{l}</button>
                  ))}
                  <PhotoInput
                    value={att[w.id]?.photo || null}
                    onChange={(photo) => setAtt((p) => ({ ...p, [w.id]: { ...(p[w.id] || { status: 'present' }), photo } }))}
                    small
                    label="ছবি" hint="" />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-hidden">
        <table className="charm-table"><thead><tr><th>Name</th><th>{t.present}</th><th>Rate</th></tr></thead>
          <tbody>{list.map((w) => (<tr key={w.id}><td><div className="flex items-center gap-3"><div className="w-8 h-8 rounded-full bg-[#EFF6FF] text-[#3B82F6] flex items-center justify-center text-xs font-bold">{w.name[0]}</div><span className="font-medium text-[#0F172A]">{w.name}</span></div></td><td>{w.present_days} {t.days}</td><td className="font-medium">৳{w.rate_per_day}</td></tr>))}</tbody>
        </table>
        {list.length === 0 && <div className="text-center py-12 text-[#94A3B8] text-sm">{t.emptyW}</div>}
      </div>

      <Modal open={show} onClose={() => setShow(false)} title={t.newWorker}>
        <form onSubmit={addWorker} className="space-y-3.5">
          {err && <div className="text-sm bg-rose-50 text-rose-600 px-3 py-2 rounded-xl">⚠️ {err}</div>}
          <div><label className="label">{t.name} <span className="text-rose-500">*</span></label><input className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">{t.phone}</label><input className="input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} inputMode="tel" /></div>
            <div><label className="label">{t.rate}</label><input className="input" type="number" min="0" value={form.rate_per_day} onChange={(e) => setForm({ ...form, rate_per_day: e.target.value })} /></div>
          </div>
          <button className="btn-primary w-full py-3">{t.add}</button>
        </form>
      </Modal>
    </div>
  );
}

export function MyStock() {
  const { lang } = useLang(); const t = T[lang];
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/contractor/stock').then(({ data }) => setData(data)).catch(() => {}); }, []);
  if (!data) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[#F59E0B] border-t-transparent rounded-full animate-spin" /></div>;
  return (
    <div className="space-y-5">
      <h1 className="text-xl font-semibold text-[#0F172A]">{t.myStock}</h1>
      <div className="grid gap-3">
        {data.totals.map((tr, i) => (
          <div key={i} className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-4 flex justify-between items-center">
            <div className="flex items-center gap-3"><div className="w-9 h-9 rounded-lg bg-[#FFFBEB] text-[#F59E0B] flex items-center justify-center">📦</div><span className="font-medium text-[#0F172A]">{tr.material_name}</span></div>
            <span className="text-lg font-bold text-[#0F172A]">{tr.qty} <span className="text-xs text-[#94A3B8]">{tr.unit}</span></span>
          </div>
        ))}
        {data.totals.length === 0 && <div className="text-center py-12 text-[#94A3B8] text-sm">{t.empty}</div>}
      </div>
      <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-5">
        <h3 className="font-semibold text-[#0F172A] mb-3">{t.history}</h3>
        <div className="divide-y divide-[#F1F5F9]">{data.stock.map((s) => (<div key={s.id} className="py-3 flex justify-between"><div><div className="text-sm font-medium text-[#0F172A]">{s.material_name}</div><div className="text-xs text-[#94A3B8]">{s.work_description || '—'} • {s.created_at?.slice(0, 10)}</div></div><div className="text-sm font-bold text-[#0F172A]">{s.qty} {s.unit}</div></div>))}</div>
      </div>
    </div>
  );
}

export function MyAttendance() {
  const { lang } = useLang(); const t = T[lang];
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [list, setList] = useState([]);
  useEffect(() => { api.get(`/contractor/attendance?date=${date}`).then(({ data }) => setList(data.attendance)).catch(() => {}); }, [date]);
  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between"><h1 className="text-xl font-semibold text-[#0F172A]">{t.attendance}</h1><input type="date" className="input w-auto" value={date} onChange={(e) => setDate(e.target.value)} /></div>
      <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-hidden">
        <table className="charm-table"><thead><tr><th>Worker</th><th>Status</th></tr></thead>
          <tbody>{list.map((w, i) => (<tr key={i}><td className="font-medium text-[#0F172A]">{w.worker_name}</td><td><Badge color={w.status || 'ink'}>{w.status || '—'}</Badge></td></tr>))}</tbody>
        </table>
        {list.length === 0 && <div className="text-center py-12 text-[#94A3B8] text-sm">{lang === 'bn' ? 'এই দিনে কোনো তথ্য নেই।' : 'No data for this date.'}</div>}
      </div>
    </div>
  );
}

export function MyBill() {
  const { lang } = useLang(); const t = T[lang];
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/contractor/profile').then(({ data }) => setData(data)).catch(() => {});
  }, []);
  if (!data) return <div className="flex items-center justify-center py-20"><div className="w-8 h-8 border-2 border-[#F59E0B] border-t-transparent rounded-full animate-spin" /></div>;
  const isContract = data.contractor.billing === 'contract';

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-semibold text-[#0F172A] flex items-center gap-2"><FaMoneyBillWave className="text-brand-500" /> {t.bill}</h1>

      {isContract ? (
        <>
          <div className="grid grid-cols-3 gap-2 text-center">
            <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.08)] py-3">
              <div className="text-[10px] text-[#94A3B8] uppercase font-semibold">{t.billed}</div>
              <div className="font-bold text-[#0F172A]">{fmt(data.bill_totals.billed)}</div>
            </div>
            <div className="bg-emerald-50 rounded-xl py-3">
              <div className="text-[10px] text-[#94A3B8] uppercase font-semibold">{t.taken}</div>
              <div className="font-bold text-emerald-700">{fmt(data.bill_totals.paid)}</div>
            </div>
            <div className="bg-amber-50 rounded-xl py-3">
              <div className="text-[10px] text-[#94A3B8] uppercase font-semibold">{t.due}</div>
              <div className="font-bold text-amber-700">{fmt(data.bill_totals.billed - data.bill_totals.paid)}</div>
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.08)] p-5">
            <h3 className="font-semibold text-[#0F172A] mb-3">{t.bill}</h3>
            {data.bills.length === 0 && <div className="text-center py-10 text-[#94A3B8] text-sm">{t.noBill}</div>}
            <div className="divide-y divide-[#F1F5F9]">
              {data.bills.map((b) => (
                <div key={b.id} className="py-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-semibold text-[#0F172A]">{b.week_start}</span>
                    <span className="text-sm font-bold text-[#0F172A]">{fmt(b.amount)}</span>
                  </div>
                  {b.work_summary && <div className="text-xs text-[#94A3B8] mt-0.5">{b.work_summary}</div>}
                  <div className="flex items-center justify-between mt-1.5 text-[11px]">
                    <Badge color={b.status}>{b.status}</Badge>
                    <span className="text-[#94A3B8]">{t.taken}: <b className="text-[#0F172A]">{fmt(b.amount_paid)}</b>{b.amount_paid < b.amount && <> • {t.due}: <b className="text-amber-600">{fmt(b.amount - b.amount_paid)}</b></>}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <div className="bg-white rounded-xl shadow-[0_1px_3px_rgba(0,0,0,0.08)] overflow-hidden">
          <table className="charm-table"><thead><tr><th>Worker</th><th>{t.week}</th><th>Days</th><th>Amount</th><th>Status</th></tr></thead>
            <tbody>{data.workers.map((w) => (
              (() => {
                const sal = null; // salary rows not in profile for daily; use worker list w/ present_days
                return (
                  <tr key={w.id}><td className="font-medium text-[#0F172A]">{w.name}</td><td>—</td><td>{w.present_days}</td><td className="font-bold">{fmt(w.rate_per_day * w.present_days)}</td><td><Badge color="pending">—</Badge></td></tr>
                );
              })()
            ))}</tbody>
          </table>
          {data.workers.length === 0 && <div className="text-center py-12 text-[#94A3B8] text-sm">{t.noBill}</div>}
        </div>
      )}
    </div>
  );
}